import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { BiometricFeatures } from '@/types/keystroke';
import MetricChip from '@/components/MetricChip';
import WaveformViz from '@/components/WaveformViz';
import StatusBadge from '@/components/StatusBadge';
import {
  getWpmLabel,
  getAccuracyLabel,
  getConsistencyLabel,
  computeSessionQuality,
} from '@/lib/score-utils';

interface SessionResultState {
  features: BiometricFeatures;
  typedText: string;
  events: number;
}

/**
 * SessionResult — Displays the result of a typing session with
 * detailed feedback, metrics, and quality assessment.
 */
export default function SessionResult() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as SessionResultState | null;

  // Fallback: try sessionStorage
  const features: BiometricFeatures | null = state?.features ??
    (() => {
      try {
        const stored = sessionStorage.getItem('hks_session_result');
        if (stored) return JSON.parse(stored).features;
      } catch {}
      return null;
    })();

  if (!features) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-txt-muted font-sans mb-4">Nenhum resultado encontrado.</p>
          <button
            onClick={() => navigate('/enrollment')}
            className="text-accent-brand hover:text-accent-brand/80 font-sans text-sm"
          >
            Voltar ao cadastro
          </button>
        </div>
      </div>
    );
  }

  const quality = computeSessionQuality(features.wpm, features.accuracy, features.consistency);

  const qualityColor =
    quality.score >= 80
      ? '#00d26a'
      : quality.score >= 60
        ? '#4fc3f7'
        : quality.score >= 40
          ? '#f5a623'
          : '#e74c3c';

  return (
    <div className="min-h-screen flex flex-col items-center px-6 py-12">
      {/* Header */}
      <div className="text-center mb-8 animate-fade-in">
        <h1 className="text-2xl font-bold text-txt-primary font-sans mb-2">
          Resultado da Sessao
        </h1>
        <StatusBadge
          label={quality.label}
          variant={
            quality.score >= 80
              ? 'success'
              : quality.score >= 60
                ? 'info'
                : quality.score >= 40
                  ? 'warning'
                  : 'danger'
          }
          size="md"
        />
      </div>

      {/* Quality score */}
      <div className="text-center mb-8 animate-slide-up">
        <div
          className="text-6xl font-bold font-mono"
          style={{ color: qualityColor }}
        >
          {quality.score}%
        </div>
        <span className="text-sm text-txt-muted font-sans">Qualidade da sessao</span>
      </div>

      {/* Main metrics */}
      <div className="grid grid-cols-3 gap-6 max-w-md w-full mb-8 animate-slide-up">
        <div className="text-center p-4 bg-bg-secondary border border-bg-surface rounded-card">
          <span className="text-2xl font-bold font-mono text-accent-info">
            {Math.round(features.wpm)}
          </span>
          <span className="block text-xs text-txt-muted font-sans mt-1">WPM</span>
          <span className="block text-[10px] text-txt-muted/60 font-sans">
            {getWpmLabel(features.wpm)}
          </span>
        </div>
        <div className="text-center p-4 bg-bg-secondary border border-bg-surface rounded-card">
          <span
            className="text-2xl font-bold font-mono"
            style={{
              color:
                features.accuracy >= 0.95
                  ? '#00d26a'
                  : features.accuracy >= 0.85
                    ? '#f5a623'
                    : '#e74c3c',
            }}
          >
            {(features.accuracy * 100).toFixed(0)}%
          </span>
          <span className="block text-xs text-txt-muted font-sans mt-1">Precisao</span>
          <span className="block text-[10px] text-txt-muted/60 font-sans">
            {getAccuracyLabel(features.accuracy)}
          </span>
        </div>
        <div className="text-center p-4 bg-bg-secondary border border-bg-surface rounded-card">
          <span
            className="text-2xl font-bold font-mono"
            style={{
              color: features.consistency >= 0.7 ? '#00d26a' : '#f5a623',
            }}
          >
            {(features.consistency * 100).toFixed(0)}%
          </span>
          <span className="block text-xs text-txt-muted font-sans mt-1">Consistencia</span>
          <span className="block text-[10px] text-txt-muted/60 font-sans">
            {getConsistencyLabel(features.consistency)}
          </span>
        </div>
      </div>

      {/* Detailed metrics */}
      <div className="w-full max-w-lg mb-8 animate-fade-in">
        <h3 className="text-sm font-medium text-txt-primary font-sans mb-3">
          Detalhes da Analise
        </h3>
        <div className="flex flex-wrap gap-2">
          <MetricChip label="Pressao media" value={`${features.dwellTime.mean.toFixed(0)}ms`} color="#7c5cfc" />
          <MetricChip label="Pressao mediana" value={`${features.dwellTime.median.toFixed(0)}ms`} color="#7c5cfc" />
          <MetricChip label="Variacao" value={`${features.dwellTime.mad.toFixed(0)}ms`} color="#7c5cfc" />
          <MetricChip label="Fluidez" value={`${features.flightTimeUD.mean.toFixed(0)}ms`} color="#4fc3f7" />
          <MetricChip label="Cadencia" value={`${features.flightTimeDD.mean.toFixed(0)}ms`} color="#4fc3f7" />
          <MetricChip label="Regularidade" value={features.iam.toFixed(2)} color="#e2b714" />
          <MetricChip label="Sobreposicao" value={`${features.overlapMean.toFixed(0)}ms`} color="#f5a623" />
          <MetricChip label="Correcoes" value={features.errorRecovery.backspaceCount} color="#e74c3c" />
          <MetricChip label="Pausas" value={features.microPauses.length} color="#646681" />
          <MetricChip label="Padroes curtos" value={features.bigrams.length} color="#00d26a" />
          <MetricChip label="Padroes longos" value={features.trigrams.length} color="#00d26a" />
          <MetricChip label="Total teclas" value={features.totalKeystrokes} color="#e8e8e8" />
          <MetricChip
            label="Duracao"
            value={`${(features.sessionDuration / 1000).toFixed(1)}s`}
            color="#e8e8e8"
          />
        </div>
      </div>

      {/* Waveform */}
      <div className="w-full max-w-lg mb-8 animate-fade-in">
        <h3 className="text-sm font-medium text-txt-primary font-sans mb-3">
          Forma de Onda do Ritmo
        </h3>
        <WaveformViz
          data={features.flightTimeUD.values}
          width={500}
          height={80}
          color="#7c5cfc"
          maxPoints={features.flightTimeUD.values.length}
          animate={false}
        />
      </div>

      {/* Error recovery */}
      {features.errorRecovery.backspaceCount > 0 && (
        <div className="w-full max-w-lg mb-8 p-4 bg-bg-secondary border border-bg-surface rounded-card animate-fade-in">
          <h3 className="text-sm font-medium text-txt-primary font-sans mb-2">
            Padrao de Correcao
          </h3>
          <div className="grid grid-cols-2 gap-3 text-xs font-sans">
            <div className="flex justify-between">
              <span className="text-txt-muted">Correcoes</span>
              <span className="text-txt-primary font-mono">{features.errorRecovery.backspaceCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-txt-muted">Taxa de correcao</span>
              <span className="text-txt-primary font-mono">
                {(features.errorRecovery.errorRate * 100).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-txt-muted">Hesitacao</span>
              <span className="text-txt-primary font-mono">
                {features.errorRecovery.meanPauseBeforeBackspace.toFixed(0)}ms
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-txt-muted">Ritmo de correcao</span>
              <span className="text-txt-primary font-mono">
                {features.errorRecovery.meanBackspaceRhythm.toFixed(0)}ms
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-4 mt-4">
        <button
          onClick={() => navigate('/enrollment')}
          className="bg-accent-brand hover:bg-accent-brand/90 text-white px-6 py-2.5 rounded-lg font-sans font-medium text-sm transition-colors"
        >
          Voltar ao Cadastro
        </button>
        <button
          onClick={() => navigate('/typing-session')}
          className="border border-bg-surface hover:border-accent-brand/50 text-txt-primary px-6 py-2.5 rounded-lg font-sans font-medium text-sm transition-colors"
        >
          Repetir Sessao
        </button>
      </div>
    </div>
  );
}
